/**
 * Utility functions for generating consistent filenames across the application
 */

/**
 * Platform URL detection - returns the actual domain name for the platform
 */
export const detectPlatformDomain = (url: string): string => {
  // Video Platforms
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube.com';
  if (url.includes('tiktok.com')) return 'tiktok.com';
  if (url.includes('instagram.com') || url.includes('instagr.am')) return 'instagram.com';
  if (url.includes('twitter.com') || url.includes('x.com') || url.includes('t.co')) return 'twitter.com';
  if (url.includes('facebook.com') || url.includes('fb.com') || url.includes('fb.watch')) return 'facebook.com';
  if (url.includes('vimeo.com')) return 'vimeo.com';
  if (url.includes('dailymotion.com')) return 'dailymotion.com';
  if (url.includes('twitch.tv')) return 'twitch.tv';
  if (url.includes('linkedin.com')) return 'linkedin.com';
  if (url.includes('pinterest.com') || url.includes('pin.it')) return 'pinterest.com';
  if (url.includes('snapchat.com')) return 'snapchat.com';
  if (url.includes('reddit.com') || url.includes('redd.it')) return 'reddit.com';
  if (url.includes('tumblr.com')) return 'tumblr.com';

  // Chinese Video Platforms
  if (url.includes('bilibili.com') || url.includes('b23.tv') || url.includes('m.bilibili.com') || url.includes('space.bilibili.com')) return 'bilibili.com';
  if (url.includes('xiaohongshu.com') || url.includes('xhscdn.com') || url.includes('xhslink.com')) return 'xiaohongshu.com';
  if (url.includes('douyin.com')) return 'douyin.com';
  if (url.includes('kuaishou.com')) return 'kuaishou.com';
  if (url.includes('weibo.com') || url.includes('weibo.cn')) return 'weibo.com';

  // Audio & Podcast Platforms
  if (url.includes('spotify.com') || url.includes('spoti.fi')) return 'spotify.com';
  if (url.includes('podcasts.apple.com') || url.includes('itunes.apple.com')) return 'podcasts.apple.com';
  if (url.includes('podcasts.google.com')) return 'podcasts.google.com';
  if (url.includes('soundcloud.com') || url.includes('snd.sc')) return 'soundcloud.com';
  if (url.includes('castbox.fm')) return 'castbox.fm';
  if (url.includes('player.fm')) return 'player.fm';
  if (url.includes('tunein.com')) return 'tunein.com';

  // Chinese Audio Platforms
  if (url.includes('xiaoyuzhoufm.com')) return 'xiaoyuzhoufm.com';
  if (url.includes('ximalaya.com') || url.includes('xmly.com')) return 'ximalaya.com';
  if (url.includes('lizhi.fm')) return 'lizhi.fm';
  if (url.includes('qingting.fm')) return 'qingting.fm';

  return 'unknown.com';
};

/**
 * Get the current frontend origin (protocol + hostname + port)
 * Replaces 'http' with 'https' for consistency in dev environments
 */
export const getCurrentOrigin = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown_host';
  }
  
  // Use window.location.origin for a reliable protocol, hostname, and port
  let origin = window.location.origin;

  // In some dev setups, origin might be http://localhost:5173, but we use https.
  // This ensures consistency.
  if (origin.startsWith('http://localhost')) {
    origin = origin.replace('http://', 'https://');
  }

  // Clean up for filename usage (replace colons, slashes)
  return origin
    .replace(/https?:\/\//, '')
    .replace(':', '_');
};

/**
 * Clean title for filename usage
 * Preserves Unicode characters for Chinese platforms
 */
export const cleanTitleForFilename = (title: string, sourceUrl: string): string => {
  if (!title) return 'untitled';

  // Platforms that should preserve Unicode characters
  const unicodePlatforms = ['xiaohongshu.com', 'bilibili.com', 'xiaoyuzhoufm.com', 'ximalaya.com', 'lizhi.fm', 'qingting.fm', 'podcasts.apple.com'];
  const platformDomain = detectPlatformDomain(sourceUrl);
  const preserveUnicode = unicodePlatforms.includes(platformDomain);

  if (preserveUnicode) {
    // Only remove characters that are invalid in filenames, keep Unicode characters
    return title.replace(/[\\/*?:"<>|]/g, '').replace(/\s+/g, '_').substring(0, 100);
  } else {
    // For non-Unicode platforms, preserve more characters but still make filename-safe
    // Keep parentheses, hyphens, periods, and convert spaces to underscores
    return title
      .replace(/[\\/*?:"<>|]/g, '') // Remove invalid filename characters
      .replace(/\s+/g, '_') // Convert spaces to underscores
      .substring(0, 80); // Longer limit to match expected pattern
  }
};

/**
 * Format bitrate consistently
 */
export const formatBitrate = (bitrate?: number): string => {
  if (!bitrate || bitrate === 0) return 'unknown';

  if (bitrate > 1000) {
    return `${Math.round(bitrate / 1000 * 10) / 10}Mbps`;
  } else {
    return `${Math.round(bitrate)}kbps`;
  }
};

/**
 * Generate filename following the pattern: ${domain.host}_${video.host}_${video.title}_${video.resolution}_${video.bitrate}.${ext}
 */
export const generateFilename = (
  title: string,
  sourceUrl: string,
  resolution?: string,
  bitrate?: number,
  ext?: string
): string => {
  // Include port in hostname for development environments
  const currentDomain = getCurrentOrigin();
  const sourceDomain = detectPlatformDomain(sourceUrl);
  const cleanTitle = cleanTitleForFilename(title, sourceUrl);
  const cleanResolution = resolution ? resolution.replace(/\s+/g, '') : 'unknown';
  const bitrateStr = formatBitrate(bitrate);
  const extension = ext || 'mp4';

  // Assemble filename parts with underscores as specified
  const parts = [currentDomain, sourceDomain, cleanTitle, cleanResolution, bitrateStr];
  const validParts = parts.filter(part => part && part !== 'unknown');

  return `${validParts.join('_')}.${extension}`;
};

/**
 * Generate filename with all parts even if some are unknown (for consistency)
 */
export const generateFilenameStrict = (
  title: string,
  sourceUrl: string,
  resolution?: string,
  bitrate?: number,
  ext?: string
): string => {
  // Include port in hostname for development environments
  const currentDomain = getCurrentOrigin();
  const sourceDomain = detectPlatformDomain(sourceUrl);
  const cleanTitle = cleanTitleForFilename(title, sourceUrl);
  const cleanResolution = resolution ? resolution.replace(/\s+/g, '') : 'unknown';
  const bitrateStr = formatBitrate(bitrate);
  const extension = ext || 'mp4';

  // Always include all parts for consistent naming
  return `${currentDomain}_${sourceDomain}_${cleanTitle}_${cleanResolution}_${bitrateStr}.${extension}`;
}; 